import React, { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const SUBJECTS = [
  "Billing / payment",
  "Bug or error",
  "Feature request",
  "Account access",
  "Refund request",
  "Something else",
];

export default function Support() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: SUBJECTS[0],
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) return toast.error("Give me a bit more detail (5+ chars).");
    setSending(true);
    try {
      const res = await fetch(`${API}/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Send failed");
      setSent(true);
      toast.success("Ticket received. Reply usually within 24h.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-void text-white flex flex-col">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-white/10">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-signal-yellow text-black flex items-center justify-center font-mono font-bold">V</div>
          <div>
            <div className="heading-display text-lg leading-none">VIRAL/CO</div>
            <div className="font-mono text-[10px] text-zinc-500 tracking-[0.25em] mt-1">CONTENT MACHINE</div>
          </div>
        </Link>
        <Link to={user ? "/app" : "/"} className="font-mono text-[11px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white transition-colors">
          ← Back
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="max-w-xl w-full" data-testid="support-page">
          {sent ? (
            <div className="brutal-border bg-[#141414] p-10 text-center space-y-4" data-testid="support-success">
              <CheckCircle2 size={48} className="text-signal-yellow mx-auto" />
              <h1 className="heading-display text-3xl">TICKET RECEIVED.</h1>
              <p className="font-mono text-xs text-zinc-400">
                We&apos;ll reply to <span className="text-white">{form.email}</span> within 24 hours (usually faster).
              </p>
              <Link
                to={user ? "/app" : "/"}
                data-testid="support-back-cta"
                className="inline-block bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-signal-yellow-hover transition-colors"
              >
                Back to the engine →
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6" data-testid="support-form">
              <div>
                <div className="label-eyebrow">// contact</div>
                <h1 className="heading-display text-4xl sm:text-5xl mt-2">TALK <span className="text-signal-yellow">TO US</span>.</h1>
                <p className="font-mono text-xs text-zinc-500 mt-3">
                  // billing, bugs, refunds, ideas — anything. we reply within 24h.
                </p>
              </div>

              <label className="block space-y-2">
                <span className="label-eyebrow">Name</span>
                <input
                  data-testid="support-name"
                  type="text" required value={form.name} onChange={onChange("name")}
                  className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none p-3 font-mono text-sm"
                />
              </label>

              <label className="block space-y-2">
                <span className="label-eyebrow">Email</span>
                <input
                  data-testid="support-email"
                  type="email" required value={form.email} onChange={onChange("email")}
                  className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none p-3 font-mono text-sm"
                />
              </label>

              <label className="block space-y-2">
                <span className="label-eyebrow">Subject</span>
                <select
                  data-testid="support-subject"
                  value={form.subject} onChange={onChange("subject")}
                  className="w-full bg-[#141414] border border-white/10 text-white focus:border-signal-yellow focus:outline-none p-3 font-mono text-sm"
                >
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>

              <label className="block space-y-2">
                <span className="label-eyebrow">Message</span>
                <textarea
                  data-testid="support-message"
                  required minLength={5} maxLength={4000}
                  value={form.message} onChange={onChange("message")}
                  placeholder="Describe what's going on — the more detail, the faster we can help."
                  className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 focus:border-signal-yellow focus:outline-none p-4 font-mono text-sm min-h-[140px]"
                />
              </label>

              <button
                type="submit" data-testid="support-submit" disabled={sending}
                className="w-full bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-signal-yellow-hover transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                <Send size={13} /> {sending ? "Sending..." : "Send ticket →"}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
